import React, { useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Delete,
  ExpandLess,
  ExpandMore,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import {
  Groups,
  RecordUserDataModel,
  Roles,
  UserGroup,
  UserProfiles,
} from "../../../type/response/RecordUserDataInitResponse";
import {
  columnLabels,
  formDefaultValue,
  handleFileImport,
  inputPattern,
} from "../UserManagement";
import UserManageTable from "./UserManageTable";
import dayjs from "dayjs";

/**
 * ユーザー情報登録・編集コンポーネント
 */
const UserProfilesRender = () => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const {
    control,
    formState: { errors },
  } = useFormContext<RecordUserDataModel>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "userProfiles",
  });
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: 1,
        }}
      >
        <Typography variant="body2">ユーザー情報</Typography>
        <IconButton
          type="button"
          color="primary"
          onClick={() =>
            append({
              id: null,
              key: "",
              value: "",
              isDeleted: false,
            } as UserProfiles)
          }
        >
          <Add fontSize="small" />
        </IconButton>
      </Box>
      {fields.map((_, index) => (
        <Grid
          container
          columnGap={0.5}
          rowGap={1}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            ml: 1,
            my: 2,
          }}
        >
          <Grid>
            <Controller
              control={control}
              name={`userProfiles.${index}.key`}
              rules={{ required: "この項目は入力必須です。" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                  required
                  error={!!errors.userProfiles?.[index]?.key}
                  helperText={errors.userProfiles?.[index]?.key?.message}
                  label="キー"
                  sx={{ width: 100 }}
                  size={isMobile ? "small" : "medium"}
                />
              )}
            />
          </Grid>
          <Grid>
            <Typography variant="body2">:</Typography>
          </Grid>
          <Grid>
            <Controller
              control={control}
              name={`userProfiles.${index}.value`}
              rules={{ required: "この項目は入力必須です。" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                  required
                  error={!!errors.userProfiles?.[index]?.value}
                  helperText={errors.userProfiles?.[index]?.value?.message}
                  label="値"
                  sx={{ width: 200 }}
                  size={isMobile ? "small" : "medium"}
                />
              )}
            />
          </Grid>
          <Grid>
            <IconButton type="button" onClick={() => remove(index)}>
              <Delete fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </>
  );
};

/**
 * ユーザー所属情報登録・編集コンポーネント
 */
const UserGroupRender = ({ groupsOptions }: { groupsOptions: Groups[] }) => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const {
    control,
    formState: { errors },
  } = useFormContext<RecordUserDataModel>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "userGroup",
  });
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: 1,
        }}
      >
        <Typography variant="body2">ユーザー所属情報</Typography>
        <IconButton
          type="button"
          color="primary"
          onClick={() =>
            append({
              groups: null,
              userGroupHistories: {
                id: null,
                startDate: null,
                endDate: null,
                notes: "",
              },
            } as UserGroup)
          }
        >
          <Add fontSize="small" />
        </IconButton>
      </Box>
      {fields.map((_, index) => (
        <Grid
          container
          columnGap={0.5}
          rowGap={1}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            ml: 1,
            my: 2,
          }}
        >
          <Grid>
            <Controller
              control={control}
              name={`userGroup.${index}.groups`}
              rules={{ required: "この項目は入力必須です。" }}
              render={({ field }) => (
                <Autocomplete
                  options={groupsOptions}
                  getOptionLabel={(option: Groups) => option.name}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!!errors.userGroup?.[index]?.groups}
                      helperText={errors.userGroup?.[index]?.groups?.message}
                      label="グループ"
                      sx={{ width: 300 }}
                      size={isMobile ? "small" : "medium"}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid>
            <Typography variant="body2">:</Typography>
          </Grid>
          <Grid>
            <Controller
              control={control}
              name={`userGroup.${index}.userGroupHistories.startDate`}
              rules={{ required: "この項目は入力必須です。" }}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="YYYY/MM/DD"
                    label="所属開始日"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) =>
                      field.onChange(value ? value.toDate() : null)
                    }
                    slotProps={{
                      calendarHeader: { format: "YYYY年M月" },
                      textField: {
                        required: true,
                        error:
                          !!errors.userGroup?.[index]?.userGroupHistories
                            ?.startDate,
                        helperText:
                          errors.userGroup?.[index]?.userGroupHistories
                            ?.startDate?.message,
                        size: isMobile ? "small" : "medium",
                      },
                    }}
                    sx={{ width: 160 }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid>
            <Controller
              control={control}
              name={`userGroup.${index}.userGroupHistories.endDate`}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="YYYY/MM/DD"
                    label="所属終了日"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) =>
                      field.onChange(value ? value.toDate() : null)
                    }
                    slotProps={{
                      calendarHeader: { format: "YYYY年M月" },
                      textField: {
                        size: isMobile ? "small" : "medium",
                      },
                    }}
                    sx={{ width: 160 }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid>
            <Controller
              control={control}
              name={`userGroup.${index}.userGroupHistories.notes`}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                  label="備考"
                  sx={{ width: 300 }}
                  size={isMobile ? "small" : "medium"}
                />
              )}
            />
          </Grid>
          <Grid>
            <IconButton type="button" onClick={() => remove(index)}>
              <Delete fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </>
  );
};

/**
 * ユーザー登録・編集ダイアログコンポーネント
 * - ユーザー情報（プロフィール・所属グループ・ロール）の新規登録・一括登録を行う
 * - props: open (ダイアログ表示状態), handleClose (閉じるコールバック),
 *          rolesOptions (ロール選択肢), groupsOptions (グループ選択肢),
 *          createOrUpdateUser (保存コールバック)
 */
const CreateUserDialog = ({
  open,
  handleClose,
  rolesOptions,
  groupsOptions,
  createOrUpdateUser,
}: {
  open: boolean;
  handleClose: (completed: boolean) => void;
  rolesOptions: Roles[];
  groupsOptions: Groups[];
  createOrUpdateUser: (data: RecordUserDataModel[]) => Promise<boolean>;
}) => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const visibleColumns = new Set(Object.keys(columnLabels));
  const pageSize = 10;

  const [showPassword, setShowPassword] = useState(false);
  const [showUserAddComponent, setShowUserAddComponent] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false); //キャンセル確認ダイアログ
  const addCount = useRef(0);

  const form = useForm<RecordUserDataModel>({
    mode: "onBlur",
    defaultValues: formDefaultValue,
  });
  const {
    control,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;
  const [rows, setRows] = useState<RecordUserDataModel[]>([]);
  const fileImportRef = useRef<HTMLInputElement>(null);

  /**
   * フォームとユーザー登録用テーブルをクリアして
   * ユーザー新規登録ダイアログを閉じる
   */
  const clearFormAndClose = () => {
    reset(); //フォームの入力内容をクリア
    setRows(() => []); //登録ユーザーリストをクリア
    setSelectedRows(new Set()); //ユーザー登録テーブルの選択状態を解除
    handleClose(true); //新規ユーザー登録ダイアログを閉じる
  };

  /**
   * ユーザー一括登録
   */
  const handleCreateUser = async () => {
    const result = await createOrUpdateUser(
      rows.map((v) => ({ ...v, userId: null }))
    );
    if (result) {
      clearFormAndClose();
    }
  };

  /**
   * キャンセルボタン押下時
   */
  const handleCancelClick = () => {
    setShowCancelDialog(true); // 確認ダイアログを表示
  };

  /**
   * キャンセル確認ダイアログ
   */
  const handleCancelDialogClose = (isClearInput: boolean) => {
    // 「破棄」を選択した場合はフォーム内容をクリアする。
    // ダイアログを閉じる。
    if (isClearInput) {
      clearFormAndClose();
    }
    //「戻る」を選択した場合
    setShowCancelDialog(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>ユーザー登録</DialogTitle>
        <DialogContent>
          <FormProvider {...form}>
            {showUserAddComponent && (
              <Grid container columnGap={1} rowGap={2} sx={{ m: 1 }}>
                <Grid>
                  <TextField
                    required
                    {...register("lastName", {
                      required: "この項目は入力必須です。",
                    })}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    label="名字"
                    sx={{ width: 124 }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid>
                  <TextField
                    {...register("middleName")}
                    label="ミドルネーム"
                    sx={{ width: 124 }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid>
                  <TextField
                    required
                    {...register("firstName", {
                      required: "この項目は入力必須です。",
                    })}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    label="名前"
                    sx={{ width: 124 }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid>
                  <TextField
                    {...register("nickname")}
                    label="ニックネーム"
                    sx={{ width: 124 }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid>



                  <TextField
                    required
                    {...register("email", {
                      required: "この項目は入力必須です。",
                      pattern: { value: inputPattern.email, message: "フォーマットが正しくありません。" },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    label="メールアドレス"
                    sx={{ width: 220 }}
                    size={isMobile ? "small" : "medium"}
                    // ⬇️ inputProps（非推奨）の代わりに slotProps を使用
                    slotProps={{
                      input: { autoComplete: "off" }, // ブラウザの自動補完抑止
                    }}
                  />



                </Grid>
                <Grid>


                  <TextField
                    required
                    {...register("password", {
                      required: "この項目は入力必須です。",
                      pattern: {
                        value: inputPattern.password,
                        message:
                          "フォーマットが正しくありません。英大文字、小文字、数字、記号を含む8文字以上で入力してください。",
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    label="初期パスワード"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    sx={{ width: 220, "& input::-ms-reveal": { display: "none" } }}
                    size={isMobile ? "small" : "medium"}
                    slotProps={{
                      // ⬇️ ここで `new-password` を指定
                      input: {
                        autoComplete: "new-password",

                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword((show) => !show)}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />


                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="userRoles"
                    rules={{ required: "この項目は入力必須です。" }}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={rolesOptions}
                        getOptionLabel={(option: Roles) => option.name}
                        value={field.value || []}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            error={!!errors.userRoles}
                            helperText={errors.userRoles?.message}
                            label="ユーザーロール"
                            sx={{ width: 300 }}
                            size={isMobile ? "small" : "medium"}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <UserProfilesRender />
                </Grid>
                <Grid size={12}>
                  <UserGroupRender groupsOptions={groupsOptions} />
                </Grid>
                <Grid
                  size={12}
                  sx={{ display: "flex", justifyContent: "flex-end", mx: 1 }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit(() => {
                      const newUserData = getValues();
                      const newUserId = `${rows.length}`;

                      // 新しいユーザーデータをディープコピーして、参照の共有を避ける
                      const deepCopiedUser: RecordUserDataModel = {
                        ...newUserData,
                        userId: newUserId,
                        // userProfiles配列をディープコピー
                        userProfiles: newUserData.userProfiles
                          ? newUserData.userProfiles.map(profile => ({ ...profile }))
                          : null,
                        // userGroup配列をディープコピー
                        userGroup: newUserData.userGroup
                          ? newUserData.userGroup.map(group => ({
                            ...group,
                            groups: group.groups ? { ...group.groups } : null,
                            userGroupHistories: group.userGroupHistories
                              ? { ...group.userGroupHistories }
                              : null,
                          }))
                          : null,
                        // userRoles配列をディープコピー
                        userRoles: newUserData.userRoles
                          ? newUserData.userRoles.map(role => ({ ...role }))
                          : null,
                      };

                      setRows((prevRows) => [...prevRows, deepCopiedUser]);
                      addCount.current += 1;
                    })}
                  >
                    追加
                  </Button>
                </Grid>
              </Grid>
            )}
          </FormProvider>
          <Divider>
            <IconButton onClick={() => setShowUserAddComponent((show) => !show)}>
              {showUserAddComponent ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Divider>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mx: 1 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                {selectedRows.size > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => {
                      setRows((rows) =>
                        rows
                          .filter((row) => !selectedRows.has(row.userId ?? ""))
                          .map((row, index) => ({
                            ...row,
                            userId: index.toString(),
                          }))
                      );
                      setSelectedRows(new Set());
                    }}
                  >
                    削除
                  </Button>
                )}
              </Box>
              {/*TODO: CSVインポートの仕様見直しのため一時的にコメントアウト 
             <Button
              variant="outlined"
              onClick={() => fileImportRef.current?.click()}
            >
              CSVインポート
            </Button> */}
              <input
                hidden
                aria-label="fileImport"
                type="file"
                onChange={(e) =>
                  handleFileImport(e, rolesOptions, groupsOptions, setRows)
                }
                accept=".csv"
                ref={fileImportRef}
              />
            </Box>
            {rows.length > 0 && (
              <UserManageTable
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                filteredData={rows}
                rows={rows}
                setRows={setRows}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                visibleColumns={visibleColumns}
                rolesOptions={rolesOptions}
                groupsOptions={groupsOptions}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button variant="outlined" onClick={handleCancelClick}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={rows.length <= 0}
            >
              登録
            </Button>
          </Box>
        </DialogActions>
      </Dialog>


      {/* キャンセル確認ダイアログ */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>キャンセル確認</DialogTitle>
        <DialogContent>
          入力内容は保存されていません。入力を破棄しますか?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCancelDialogClose(false)}>戻る</Button>
          <Button color="error" onClick={() => handleCancelDialogClose(true)}>
            破棄
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateUserDialog;
